// Patches the cached system for a system action: first with a prediction when the button is pressed,
// then with the server's response, so buttons, queues and the top bar update without waiting for the
// refetch that follows. REST stays the source of truth: the refetch (and SYSTEM_STATE_UPDATED)
// replaces this data a moment later.
import type {
  BuildingQueueItem,
  CancelBuildingResponse,
  CancelShipProductionResponse,
  FacilityType,
  FullSystem,
  PortTrade,
  PortTradeResponse,
  Resources,
  ShipProductionItem,
  ShipProductionResponse,
  ShipType,
  SystemResponse,
  TradingPortTerms,
  UpgradeBlocker,
  UpgradeResponse,
} from '@stars-and-ships/api-types';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '../api/queries';
import { liveResources } from './live';

/** Adds a refund to the stored amounts, never above the caps (the server caps the same way). */
function withRefund(system: FullSystem, refunded: Resources): FullSystem['resources'] {
  const add = (key: keyof Resources) =>
    Math.min(system.resources.caps[key], system.resources.amounts[key] + refunded[key]);
  return {
    ...system.resources,
    amounts: { metal: add('metal'), gas: add('gas'), supplies: add('supplies') },
  };
}

/** Entries of the one build queue (G-57): one building, one waiting. */
export const BUILD_QUEUE_LENGTH = 2;

const withBlockers = (
  upgrades: FullSystem['upgrades'],
  map: (blocker: UpgradeBlocker | null) => UpgradeBlocker | null
): FullSystem['upgrades'] =>
  Object.fromEntries(
    Object.entries(upgrades).map(([facility, option]) => [
      facility,
      option ? { ...option, blocker: map(option.blocker) } : option,
    ])
  );

/** A queued upgrade shows at once; a full queue blocks every other upgrade until the refetch. */
export function withUpgradeStarted(system: FullSystem, response: UpgradeResponse): FullSystem {
  const buildingQueue = [
    ...system.buildingQueue.filter((q) => q.id !== response.queueItem.id),
    response.queueItem,
  ].sort((a, b) => a.slot - b.slot);
  return {
    ...system,
    resources: response.resources,
    buildingQueue,
    upgrades:
      buildingQueue.length >= BUILD_QUEUE_LENGTH
        ? withBlockers(system.upgrades, (b) => b ?? { code: 'QUEUE_SLOTS_FULL' })
        : system.upgrades,
  };
}

export function withUpgradeCancelled(
  system: FullSystem,
  response: CancelBuildingResponse
): FullSystem {
  return {
    ...system,
    resources: withRefund(system, response.refunded),
    buildingQueue: system.buildingQueue.filter((q) => q.id !== response.cancelledId),
    upgrades: withBlockers(system.upgrades, (b) => (b?.code === 'QUEUE_SLOTS_FULL' ? null : b)),
  };
}

export const withPortTradeStarted = (
  system: FullSystem,
  response: PortTradeResponse
): FullSystem => ({ ...system, portTrade: response.trade, resources: response.resources });

export const withProductionStarted = (
  system: FullSystem,
  response: ShipProductionResponse
): FullSystem => ({
  ...system,
  resources: response.resources,
  habitat: response.habitat,
  shipProduction: [
    ...system.shipProduction.filter((i) => i.id !== response.item.id),
    response.item,
  ].sort((a, b) => a.position - b.position),
});

export const withProductionCancelled = (
  system: FullSystem,
  response: CancelShipProductionResponse
): FullSystem => ({
  ...system,
  resources: withRefund(system, response.refunded),
  shipProduction: system.shipProduction.filter((i) => i.id !== response.cancelledId),
});

/** Provisional entries carry this id prefix until the server's answer replaces them (spec §3.8). */
export const PENDING_ID_PREFIX = 'pending:';
export const isPendingId = (id: string): boolean => id.startsWith(PENDING_ID_PREFIX);

/** Credits a trade of `total` resources yields at these terms (rounded down, as the server does). */
export const tradeCredits = (terms: TradingPortTerms, total: number): number =>
  Math.floor((total * terms.creditsPer1000) / 1000);

export const batchCost = (cost: Resources, quantity: number): Resources => ({
  metal: cost.metal * quantity,
  gas: cost.gas * quantity,
  supplies: cost.supplies * quantity,
});

const iso = (ms: number) => new Date(ms).toISOString();
const ZERO: Resources = { metal: 0, gas: 0, supplies: 0 };

/** The live amounts at `now` minus a cost, re-evaluated at `now` so the counters keep animating. */
function withPaid(system: FullSystem, cost: Resources, now: number): FullSystem['resources'] {
  const have = liveResources(system.resources, now);
  const pay = (key: keyof Resources) => Math.max(0, have[key] - cost[key]);
  return {
    ...system.resources,
    amounts: { metal: pay('metal'), gas: pay('gas'), supplies: pay('supplies') },
    evaluatedAt: iso(now),
  };
}

/** The upgrade as the server will most likely answer it: paid, queued, waiting behind a running one. */
export function predictUpgradeStarted(
  system: FullSystem,
  facility: FacilityType,
  pendingId: string,
  now: number
): FullSystem {
  const option = system.upgrades[facility];
  if (!option) return system;
  const last = [...system.buildingQueue].sort((a, b) => b.slot - a.slot)[0];
  const start = last ? Math.max(now, Date.parse(last.completesAt)) : now;
  const queueItem: BuildingQueueItem = {
    id: pendingId,
    slot: (last?.slot ?? 0) + 1,
    facilityType: facility,
    targetLevel: option.targetLevel,
    startedAt: iso(start),
    completesAt: iso(start + option.durationMs),
  };
  return withUpgradeStarted(system, { queueItem, resources: withPaid(system, option.cost, now) });
}

/** The refund depends on progress (server rule), so it arrives with the answer. */
export const predictUpgradeCancelled = (system: FullSystem, itemId: string): FullSystem =>
  withUpgradeCancelled(system, { cancelledId: itemId, refunded: ZERO });

export function predictPortTradeStarted(
  system: FullSystem,
  offer: Resources,
  pendingId: string,
  now: number
): FullSystem {
  const terms = system.tradingPort;
  const trade: PortTrade = {
    id: pendingId,
    status: 'running',
    resourcesIn: offer,
    creditsOut: tradeCredits(terms, offer.metal + offer.gas + offer.supplies),
    startedAt: iso(now),
    completesAt: iso(now + terms.durationMs),
  };
  return withPortTradeStarted(system, { trade, resources: withPaid(system, offer, now) });
}

/** A batch starts at once on a free line and waits (no times yet) behind a running one (G-30). */
export function predictProductionStarted(
  system: FullSystem,
  order: { shipType: ShipType; quantity: number },
  pendingId: string,
  now: number
): FullSystem {
  const option = system.shipOptions[order.shipType];
  if (!option) return system;
  const busy = system.shipProduction.length > 0;
  const item: ShipProductionItem = {
    id: pendingId,
    shipType: order.shipType,
    quantity: order.quantity,
    delivered: 0,
    position: Math.max(0, ...system.shipProduction.map((i) => i.position)) + 1,
    unitDurationMs: option.unitDurationMs,
    startedAt: busy ? null : iso(now),
    nextShipAt: busy ? null : iso(now + option.unitDurationMs),
    completesAt: busy ? null : iso(now + option.unitDurationMs * order.quantity),
  };
  return withProductionStarted(system, {
    item,
    resources: withPaid(system, batchCost(option.cost, order.quantity), now),
    habitat: {
      ...system.habitat,
      used: system.habitat.used + option.habitatPoints * order.quantity,
    },
  });
}

/** Undelivered ships stop counting against Habitat; the refund arrives with the answer. */
export function predictProductionCancelled(system: FullSystem, itemId: string): FullSystem {
  const item = system.shipProduction.find((i) => i.id === itemId);
  const freed = item
    ? (system.shipOptions[item.shipType]?.habitatPoints ?? 0) * (item.quantity - item.delivered)
    : 0;
  return {
    ...withProductionCancelled(system, { cancelledId: itemId, refunded: ZERO, shipsDelivered: 0 }),
    habitat: { ...system.habitat, used: Math.max(0, system.habitat.used - freed) },
  };
}

/** Drops one provisional entry, so the server's answer can take its place. */
export const withoutPending = (system: FullSystem, pendingId: string): FullSystem => ({
  ...system,
  buildingQueue: system.buildingQueue.filter((q) => q.id !== pendingId),
  shipProduction: system.shipProduction.filter((i) => i.id !== pendingId),
  portTrade: system.portTrade?.id === pendingId ? null : system.portTrade,
});

/** Rewrites the cached `full` view of one system; public views and missing entries stay untouched. */
export function patchCachedSystem(
  queryClient: QueryClient,
  galaxyId: string,
  systemId: string,
  patch: (system: FullSystem) => FullSystem
): void {
  queryClient.setQueryData<SystemResponse>(queryKeys.system(galaxyId, systemId), (current) =>
    current?.tier === 'full' ? { tier: 'full', system: patch(current.system) } : current
  );
}
